import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { setupCache } from "axios-cache-interceptor";
import queryString from "query-string";

import AuthService, { AuthGrantType } from "./authService";
import { toAppUrl } from "./configService";

type CustomAxiosRequestConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
};

const authPrefix = "Bearer";
const authTpl = (authToken: string) => `${authPrefix} ${authToken}`;

const cachedAxios = setupCache(axios.create(), {
    ttl: 1000 * 60 * 5,
});

cachedAxios.interceptors.request.use(
    (config: CustomAxiosRequestConfig) => {
        const token = AuthService.getSession()?.access_token;
        if (token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = config.headers.Authorization ?? authTpl(token);
        }
        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    },
);

type AuthError = {
    error: string;
    error_description: string;
    error_uri: string;
};

let isResettingAuth = false;
// Shared in-flight refresh: every concurrent 401 awaits the same promise instead of starting
// its own refresh call or polling on a timer, so a burst of parallel requests (e.g. the
// Dashboard's prefetch queries after an overnight-expired access token) is serialized into a
// single refresh.
let refreshPromise: ReturnType<typeof AuthService.refreshSession> | null = null;
const axiosInstance = axios.create();

const resetAuth = async () => {
    // Guard so a burst of failing requests (and the failed refresh POST re-entering this
    // interceptor) can only trigger a single logout + redirect.
    if (isResettingAuth) {
        return;
    }
    isResettingAuth = true;
    // Reuse the same notice LoginPage shows after AuthService.restartLoginFlow(), so a refresh
    // failure explains itself instead of silently dumping the user back on the login form.
    AuthService.storeLoginNotice("pages:loginPage.toasts.sessionExpired");
    try {
        await AuthService.logout();
    } catch {
        // Redirect even if the logout cookie call fails. isResettingAuth is already latched
        // above, so skipping the redirect here would strand every later 401 on that guard forever.
    }
    window.location.href = toAppUrl("/login");
};

const getRefreshedToken = () => {
    refreshPromise ??= AuthService.refreshSession().finally(() => {
        refreshPromise = null;
    });
    return refreshPromise;
};

axiosInstance.interceptors.request.use(
    (config: CustomAxiosRequestConfig) => {
        const token = AuthService.getSession();
        if (token && token.access_token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = config.headers.Authorization ?? authTpl(token.access_token);
        }
        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    },
);

axiosInstance.interceptors.response.use(
    (result) => {
        return result;
    },
    async (error: AxiosError<AuthError>) => {
        const originalRequestConfig = error.config as CustomAxiosRequestConfig;
        const originalRequestConfigData = queryString.parse(originalRequestConfig.data as string);
        // The refresh-token POST itself goes through this same axiosInstance/interceptor. Treating
        // a 401 on it like any other unauthorized request would call getRefreshedToken() again
        // while the very refresh it's waiting on IS this request — the two awaits form a cycle
        // that never settles. A failed refresh-grant request always means "give up", never "go
        // refresh and retry", so it's excluded here and grouped with the other refresh-failure
        // cases below instead.
        const isRefreshTokenGrantRequest = originalRequestConfigData?.grant_type === AuthGrantType.refresh_token;
        const isUnauthorized = error?.response?.status === 401 && !isRefreshTokenGrantRequest;
        const isRefreshTokenInvalid = error?.response?.status === 400 && error?.response?.data?.error === "invalid_grant";
        const refreshTokenApiError =
            isRefreshTokenGrantRequest && (error?.response?.status === 401 || error?.response?.status === 500);

        if (isUnauthorized && !originalRequestConfig._retry) {
            // Mark as retried before awaiting the refresh so a concurrent 401 that joins this
            // same in-flight refresh (via getRefreshedToken) never re-enters this branch, even
            // if its replayed request 401s again.
            originalRequestConfig._retry = true;
            try {
                const token = await getRefreshedToken();
                if (token && token.access_token) {
                    originalRequestConfig.headers = originalRequestConfig.headers ?? {};
                    originalRequestConfig.headers.Authorization = authTpl(token.access_token);
                }
                return await axiosInstance(originalRequestConfig);
            } catch (refreshError) {
                // Any failed refresh including "no refresh token present" is unrecoverable
                // log out and redirect to /login
                await resetAuth();
                return Promise.reject(refreshError);
            }
        }

        if (isRefreshTokenInvalid || refreshTokenApiError) {
            await resetAuth();
        }

        return Promise.reject(error);
    },
);

const HttpService = {
    get: axiosInstance.get.bind(null),
    getWithCache: cachedAxios.get.bind(null) as typeof axiosInstance.get,
    post: axiosInstance.post.bind(null),
    patch: axiosInstance.patch.bind(null),
    put: axiosInstance.put.bind(null),
    delete: axiosInstance.delete.bind(null),
    axiosInstance,
};

export default HttpService;
